import React, { useState, useEffect } from "react";
import { X, CheckCheck, Loader, Bell, ExternalLink, ChevronLeft, ChevronRight, Info, AlertTriangle, AlertCircle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { ApiService } from "../../services/api";

interface Notification {
  id: string;
  title: string;
  description: string;
  type: "INFO" | "WARNING" | "ERROR" | "SUCCESS";
  path: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  department?: {
    id: string;
    name: string;
    color: string;
  };
}

const NotificationsPanel = ({ isOpen, onClose, isHomePage = false }: { isOpen: boolean; onClose?: () => void; isHomePage?: boolean }) => {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const panelPositionClass = isMobile ? "inset-0 top-[60px]" : "top-[60px] right-0 bottom-0 w-80";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setPage(1); // Reset to first page when opening
      fetchNotifications(1);
    }
  }, [isOpen]);

  const fetchNotifications = async (currentPage: number) => {
    setLoading(true);
    try {
      const response = await ApiService.getNotifications({
        page: currentPage,
        limit: limit,
        filter: "all",
      });
      setNotifications(response.results || []);
      setTotalPages(response.totalPages || 1);
      
      // Update unread count - ideally this should be a separate lightweight call or part of metadata
      // For now we rely on what we can get, or maybe a dedicated endpoint exists.
      // If the backend returns unread count in metadata, use it.
      // Otherwise, we might only know about the unread usage on the current page, which is imperfect.
      // Let's assume we want to keep the unread count accurate globally if possible.
      // If ApiService.getUnreadNotificationCount exists, use it.
      try {
         const countRes = await ApiService.getUnreadNotificationCount();
         setUnreadCount(countRes.unreadCount || 0);
      } catch {
         // Fallback to local count if endpoint fails (though it shouldn't)
      }

    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchNotifications(newPage);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await ApiService.markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    setIsMarkingAll(true);
    try {
      await ApiService.markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    if (notification.path) {
      if (onClose) onClose();
      navigate(notification.path);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "SUCCESS":
        return <CheckCircle size={20} className="text-white" />;
      case "WARNING":
        return <AlertTriangle size={20} className="text-white" />;
      case "ERROR":
        return <AlertCircle size={20} className="text-white" />;
      default:
        return <Info size={20} className="text-white" />;
    }
  };

  const getNotificationColorObj = (type: string) => {
    switch (type) {
      case "SUCCESS":
        return { bg: "bg-green-500", text: "text-green-500", border: "border-green-200", softBg: "bg-green-50" };
      case "WARNING":
        return { bg: "bg-yellow-500", text: "text-yellow-500", border: "border-yellow-200", softBg: "bg-yellow-50" };
      case "ERROR":
        return { bg: "bg-red-500", text: "text-red-500", border: "border-red-200", softBg: "bg-red-50" };
      default:
        return { bg: "bg-blue-500", text: "text-blue-500", border: "border-blue-200", softBg: "bg-blue-50" };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Az önce";
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} sa önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .notifications-container::-webkit-scrollbar {
            display: none;
          }
          .notifications-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>

      {isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />}

      {!isHomePage && <div className={`fixed z-49 bg-white ${panelPositionClass}`} />}

      <div className={`fixed z-50 ${panelPositionClass}`}>
        <div
          className={`flex flex-col h-full shadow-2xl w-full ${isHomePage ? "backdrop-blur-lg" : "bg-white"} ${
            isMobile ? "rounded-none p-0" : isHomePage ? "border border-white/25 p-1" : "border border-gray-200 p-1"
          }`}
          style={
            isHomePage
              ? {
                  background: `linear-gradient(135deg, ${currentTheme.colors.background.base}26, ${currentTheme.colors.background.via}33, ${currentTheme.colors.background.to}26)`,
                }
              : undefined
          }>
          {/* Header */}
          <div className="flex-shrink-0 px-6" style={{ paddingTop: "11px", paddingBottom: "11px" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                 <div className="relative">
                   <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                     <Bell size={16} className="text-white" />
                   </div>
                 </div>
                <div className="flex-1">
                  <h2 className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-900"}`}>Bildirimler</h2>
                  {unreadCount > 0 && (
                    <span className={`text-xs font-medium ${isHomePage ? "text-red-300" : "text-red-500"}`}>
                      {unreadCount} Okunmamış
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAll}
                    title="Tümünü okundu işaretle"
                    className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 ${
                      isHomePage ? "text-white hover:bg-white/10" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    }`}>
                    {isMarkingAll ? <Loader size={16} className="animate-spin" /> : <CheckCheck size={18} />}
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 ${
                      isHomePage
                        ? "text-white hover:bg-white/10"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                    aria-label="Kapat">
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="notifications-container flex-1 overflow-y-auto px-4 py-2 space-y-3">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full space-y-3">
                <Loader size={32} className={`animate-spin ${isHomePage ? "text-white" : "text-blue-600"}`} />
                <p className={`text-sm ${isHomePage ? "text-gray-300" : "text-gray-500"}`}>Yükleniyor...</p>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => {
                 const colors = getNotificationColorObj(notification.type);
                 return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`group relative p-3 rounded-2xl transition-all duration-300 cursor-pointer border ${
                    isHomePage
                      ? notification.isRead
                        ? "bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10"
                        : "bg-white/10 border-white/20 hover:bg-white/15 hover:border-white/25 shadow-lg shadow-black/5"
                      : notification.isRead
                      ? "bg-white border-transparent hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm"
                      : "bg-white border-blue-100 shadow-sm hover:shadow-md hover:border-blue-200 scale-[1.01]"
                  }`}>
                  <div className="flex items-start gap-3">
                    {/* Icon Box */}
                    <div
                      className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center shadow-sm transition-transform group-hover:scale-110 ${
                         colors.bg
                      }`}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`text-sm font-semibold leading-tight ${isHomePage ? "text-white group-hover:text-blue-200" : "text-gray-900 group-hover:text-blue-600"} transition-colors`}>
                          {notification.title}
                        </h3>
                         {!notification.isRead && (
                             <span className={`w-2 h-2 rounded-full ${isHomePage ? "bg-red-400" : "bg-red-500"} animate-pulse flex-shrink-0 mt-1.5`}></span>
                         )}
                      </div>
                      <p className={`text-xs leading-relaxed line-clamp-2 mb-2 ${isHomePage ? "text-gray-300 group-hover:text-white" : "text-gray-500 group-hover:text-gray-700"} transition-colors`}>
                        {notification.description}
                      </p>
                      
                      <div className="flex items-center justify-between pt-2 border-t border-dashed" style={{ borderColor: isHomePage ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }}>
                         <div className="flex items-center gap-2">
                             {/* Time */}
                             <span className={`text-xs font-medium ${isHomePage ? "text-gray-400" : "text-gray-400"}`}>
                              {formatTimeAgo(notification.createdAt)}
                            </span>
                            
                            {/* Department Badge */}
                            {notification.department && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-md font-medium tracking-wide`}
                                style={{
                                  backgroundColor: isHomePage ? `${notification.department.color}20` : `${notification.department.color}15`,
                                  color: notification.department.color,
                                  border: `1px solid ${notification.department.color}40`
                                }}>
                                {notification.department.name}
                              </span>
                            )}
                         </div>
                         
                         {/* Link Icon */}
                         {notification.path && (
                            <div className={`p-1.5 rounded-full transition-colors ${
                                isHomePage ? "hover:bg-white/20 text-gray-400 hover:text-white" : "hover:bg-gray-100 text-gray-400 hover:text-blue-600"
                            }`}>
                                <ExternalLink size={14} />
                            </div>
                         )}
                      </div>
                    </div>
                  </div>
                </div>
              )})
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isHomePage ? "bg-white/5" : "bg-gray-50 slide-in-bottom"}`}>
                     <Bell size={24} className={`opacity-50 ${isHomePage ? "text-white" : "text-gray-400"}`} />
                </div>
                <p className={`text-sm font-medium ${isHomePage ? "text-gray-300" : "text-gray-600"}`}>Henüz bildiriminiz yok</p>
                <p className={`text-xs ${isHomePage ? "text-gray-500" : "text-gray-400"}`}>Yeni bildirimler burada görünecek</p>
              </div>
            )}
            
            {/* Pagination Loading Info */}
            {loading && notifications.length > 0 && (
                <div className="text-center py-2">
                     <span className={`text-xs ${isHomePage ? "text-white/50" : "text-gray-400"}`}>Yükleniyor...</span>
                </div>
            )}
          </div>

          {/* Footer / Pagination */}
          <div className="flex-shrink-0 px-5 py-3 border-t border-dashed" style={{ borderColor: isHomePage ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }}>
            <div className="flex items-center justify-between">
                <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1 || loading}
                    className={`p-2 rounded-lg transition-all ${
                        isHomePage 
                        ? "text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent" 
                        : "text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                    }`}
                >
                    <ChevronLeft size={18} />
                </button>
                
                <span className={`text-sm font-medium ${isHomePage ? "text-gray-300" : "text-gray-600"}`}>
                    Sayfa {page} / {totalPages}
                </span>

                 <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages || loading}
                    className={`p-2 rounded-lg transition-all ${
                        isHomePage 
                        ? "text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent" 
                        : "text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                    }`}
                >
                    <ChevronRight size={18} />
                </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationsPanel;
